import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useRef } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants';
import FacetDataContext from '~/contexts/FacetDataContext';
import { useUserData } from '~/contexts/UserContext';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import useGenerateInventoryRequest from '~/hooks/useGenerateInventoryRequest';
import fetchFacetData from '~/services/fetchFacetData';
import generateErrorLogger from '~/utilities/errorHandlers';

export default function useGetFacetData(): void {
  const {
    facetData: { setData, setError, setLoading }
  } = useContext(FacetDataContext);
  const isFirstRender = useRef(true);

  const { isLoggedIn } = useUserData();
  const { request, conditionalRequestedFeatures } = useGenerateInventoryRequest(
    MAX_PAGE_SIZE_DEFAULT,
    {
      includeAnalyticsData: false,
      includePageNumber: false,
      limitRequestedFeatures: !isLoggedIn
    }
  );
  const isInExperiment = useCheckExperimentBucket(
    'srch-impossible-facets',
    'experiment'
  );

  useEffectWithController(
    controller => {
      if (!isInExperiment) return;

      if (isFirstRender.current) {
        isFirstRender.current = false;

        return;
      }

      setLoading();

      fetchFacetData(
        {
          ...request,
          requestedFeatures: request.requestedFeatures.concat(
            ...conditionalRequestedFeatures
          )
        },
        controller
      )
        .then(res => {
          setData(res.data);
        })
        .catch(
          generateErrorLogger(
            'Facet data',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      isInExperiment,
      conditionalRequestedFeatures,
      request,
      setData,
      setError,
      setLoading
    ]
  );
}
